import { Store } from '@ngxs/store';
import _ from 'lodash';
import { shortcutStringBuilder } from 'src/app/helper/shortcutStringBuilder';
import { EmailPurpose } from 'src/app/samwebDescription/samwin/EmailPurpose';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { QueueSelectionPurpose } from 'src/app/samwebDescription/samwin/QueueSelectionPurpose';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import { BrickInstancesSelectors } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.selectors';
import { TogglePilotPanel } from 'src/app/store/dashboard/pilot-panel/pilot-panel.actions';
import {
   AcceptCallFromCallList,
   AcceptRingingCallAction,
   DisconnectLineA,
   DisconnectLineB,
   EndCallAction,
   ParkCallAgent,
   ParkCallSkill,
   StartConference,
   StartNewCallAction,
   SwitchHoldLines,
   ToggleHoldLineA,
   ToggleHoldLineB,
   ToggleManualCallRecording,
} from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { StartEmailProcess } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { NextDialTargetState } from 'src/app/store/samwin/next-dial-target/next-dial-target.state';
import { StartQueueSelectionProcess } from 'src/app/store/samwin/queue-selection-dialog/queue-selection-dialog.actions';
import { SamwinCallListState } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { RequestUserStatusChange } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.actions';
import { SamwinUserStatusSelectors } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.selectors';
import { TransferCallAction } from 'src/app/store/samwin/transfer/transfer.actions';
import { TransferState } from 'src/app/store/samwin/transfer/transfer.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { TerminateCurrentSession } from 'src/app/store/session/session.actions';
import { SessionSelectors } from 'src/app/store/session/session.selectors';
import { ToggleMuteMicrophoneAction } from 'src/app/store/settings/audio-settings/audio-settings.actions';
import { ToggleGlobalSettingsDialog } from 'src/app/store/settings/settings.actions';
import { SettingsState } from 'src/app/store/settings/settings.state';
import { Originator } from 'src/generated/signalR/originator';
import { Enum, EnumType } from 'ts-jenum';
import { Key } from './Keys';

@Enum<ShortcutTypeGlobal>('value')
export class ShortcutTypeGlobal extends EnumType<ShortcutTypeGlobal>() {
   constructor(public value, public translationKey: string, public defaultHotkeyCombo: string, private hotkeyAction: (store: Store) => boolean) {
      super();
   }
   // GLOBAL
   static readonly SIGN_OUT = new ShortcutTypeGlobal('SIGN_OUT', 'navigation.hotkeys.sign_out', null, (store) => {
      const isLogoutDisabled = store.selectSnapshot(SessionSelectors.isLogoutDisabled);
      if (!isLogoutDisabled) {
         store.dispatch(new TerminateCurrentSession());
      }
      return true;
   });

   static readonly USER_SETTINGS = new ShortcutTypeGlobal('USER_SETTINGS', 'navigation.hotkeys.user_settings', null, (store) => {
      store.dispatch(new ToggleGlobalSettingsDialog());
      return true;
   });

   // NOTE: Missing Shortcuts
   //   - Malicious Call

   // CALLING
   // MAKE & ACCEPT call should work together like they would be in one CONNECT action.
   // Note: If Dialpad is not changing with incoming call, we need to check if Hotkeys are separated.
   static readonly MAKE_CALL = new ShortcutTypeGlobal('MAKE_CALL', 'navigation.hotkeys.make_call', shortcutStringBuilder(...[Key.Enter]), (store) => {
      // Try to figure out if we should Accept or make a call.
      const isCallRingingAndAcceptable = store.selectSnapshot(ClientPhoneState.isCallRingingAndAcceptable);
      const canMakeCall = store.selectSnapshot(ClientPhoneState.canMakeCall);
      const isInEditMode = store.selectSnapshot(BrickInstancesSelectors.isBrickWallEditable);
      const isSettingsPanelOpen = store.selectSnapshot(SettingsState.isSettingsDialogOpen);
      if (!isCallRingingAndAcceptable && canMakeCall && !isInEditMode && !isSettingsPanelOpen) {
         store.dispatch(new StartNewCallAction(store.selectSnapshot(NextDialTargetState.nextDialTarget)));
      }
      return true;
   });

   static readonly ACCEPT_CALL = new ShortcutTypeGlobal('ACCEPT_CALL', 'navigation.hotkeys.accept_call', shortcutStringBuilder(...[Key.Enter]), (store) => {
      // Try to figure out if we should Accept or make a call.
      const isCallRingingAndAcceptable = store.selectSnapshot(ClientPhoneState.isCallRingingAndAcceptable);
      if (isCallRingingAndAcceptable) {
         store.dispatch(new AcceptRingingCallAction());
      }
      return true;
   });

   static readonly END_CALL = new ShortcutTypeGlobal('END_CALL', 'navigation.hotkeys.end_call', shortcutStringBuilder(...[Key.NumpadAdd]), (store) => {
      store.dispatch(new EndCallAction());
      return true;
   });

   static readonly TOGGLE_PILOT_PANEL = new ShortcutTypeGlobal(
      'TOGGLE_PILOT_PANEL',
      'navigation.hotkeys.toggle_pilot_panel',
      shortcutStringBuilder(...[Key.Ctrl, Key.Alt, Key.KeyT]),
      (store) => {
         store.dispatch(new TogglePilotPanel());
         return true;
      }
   );

   static readonly DISCONNECT_LINE_A = new ShortcutTypeGlobal(
      'DISCONNECT_LINE_A',
      'navigation.hotkeys.disconnect_line_a',
      shortcutStringBuilder(...[Key.Alt, Key.KeyA]),
      (store) => {
         store.dispatch(new DisconnectLineA());
         return true;
      }
   );

   static readonly DISCONNECT_LINE_B = new ShortcutTypeGlobal(
      'DISCONNECT_LINE_B',
      'navigation.hotkeys.disconnect_line_b',
      shortcutStringBuilder(...[Key.NumpadSubtract]),
      (store) => {
         store.dispatch(new DisconnectLineB());
         return true;
      }
   );

   static readonly REQUEUE_1 = new ShortcutTypeGlobal(
      'REQUEUE_1',
      'navigation.hotkeys.requeue_call',
      shortcutStringBuilder(...[Key.Ctrl, Key.Shift, Key.Digit1]),
      (store) => {
         const call = store.selectSnapshot(SamwinCallListState.getCallByPositionFn)(1);
         store.dispatch(new StartQueueSelectionProcess(QueueSelectionPurpose.REQUEUE, call, call?.queueId));
         return true;
      }
   );

   static readonly TRANSFER_CALL = new ShortcutTypeGlobal(
      'TRANSFER_CALL',
      'navigation.hotkeys.transfer_call',
      shortcutStringBuilder(...[Key.Ctrl, Key.Enter]),
      (store) => {
         if (store.selectSnapshot(TransferState.canTransfer)) {
            store.dispatch(new TransferCallAction());
         }
         return true;
      }
   );

   static readonly HOLD_LINE_A = new ShortcutTypeGlobal(
      'HOLD_LINE_A',
      'navigation.hotkeys.hold_line_a',
      shortcutStringBuilder(...[Key.NumpadMultiply]),
      (store) => {
         if (store.selectSnapshot(SamwinLinesStateSelector.canToggleHoldLineA)) {
            store.dispatch(new ToggleHoldLineA());
         }
         return true;
      }
   );

   static readonly HOLD_LINE_B = new ShortcutTypeGlobal(
      'HOLD_LINE_B',
      'navigation.hotkeys.hold_line_b',
      shortcutStringBuilder(...[Key.NumpadDivide]),
      (store) => {
         if (store.selectSnapshot(SamwinLinesStateSelector.canToggleHoldLineB)) {
            store.dispatch(new ToggleHoldLineB());
         }
         return true;
      }
   );

   static readonly SWITCH_HOLD_LINES = new ShortcutTypeGlobal(
      'SWITCH_HOLD_LINES',
      'navigation.hotkeys.switch_hold_lines',
      shortcutStringBuilder(...[Key.Alt, Key.KeyH]),
      (store) => {
         if (store.selectSnapshot(SamwinLinesStateSelector.canSwitchHoldLines)) {
            store.dispatch(new SwitchHoldLines());
         }
         return true;
      }
   );

   static readonly START_CONFERENCE = new ShortcutTypeGlobal(
      'START_CONFERENCE',
      'navigation.hotkeys.start_conference',
      shortcutStringBuilder(...[Key.Alt, Key.KeyK]),
      (store) => {
         if (store.selectSnapshot(SamwinLinesStateSelector.canStartConference)) {
            store.dispatch(new StartConference());
         }
         return true;
      }
   );

   static readonly AGENT_PARK = new ShortcutTypeGlobal(
      'AGENT_PARK',
      'navigation.hotkeys.agent_park',
      shortcutStringBuilder(...[Key.Ctrl, Key.KeyP]),
      (store) => {
         if (store.selectSnapshot(SamwinLinesStateSelector.canCallBeParkedAtAgent)) {
            store.dispatch(new ParkCallAgent());
         }
         return true;
      }
   );

   static readonly SKILL_PARK = new ShortcutTypeGlobal(
      'SKILL_PARK',
      'navigation.hotkeys.skill_park',
      shortcutStringBuilder(...[Key.Ctrl, Key.Shift, Key.KeyP]),
      (store) => {
         if (store.selectSnapshot(SamwinLinesStateSelector.canCallBeParkedInSkill)) {
            store.dispatch(new ParkCallSkill());
         }
         return true;
      }
   );

   static readonly START_CALL_RECORDING = new ShortcutTypeGlobal(
      'START_CALL_RECORDING',
      'navigation.hotkeys.start_call_recording',
      shortcutStringBuilder(...[Key.Alt, Key.KeyR]),
      (store) => {
         store.dispatch(new ToggleManualCallRecording());
         return true;
      }
   );

   static readonly TOGGLE_MUTE_MICROPHONE = new ShortcutTypeGlobal(
      'TOGGLE_MUTE_MICROPHONE',
      'navigation.hotkeys.toggle_mute_microphone',
      shortcutStringBuilder(...[Key.Alt, Key.KeyM]),
      (store) => {
         store.dispatch(new ToggleMuteMicrophoneAction());
         return true;
      }
   );

   static readonly EXECUTE_IVR = new ShortcutTypeGlobal(
      'EXECUTE_IVR',
      'navigation.hotkeys.execute_ivr',
      shortcutStringBuilder(...[Key.Alt, Key.Ctrl, Key.Digit1]),
      (store) => {
         store.dispatch(
            new StartQueueSelectionProcess(
               QueueSelectionPurpose.EXECUTE_IVR,
               store.selectSnapshot(SamwinCallListState.getCallByIdFn)(store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo).callId)
            )
         );
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_ONE = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_ONE',
      'navigation.hotkeys.accept_call_from_call_list_one',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit1]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 1);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_TWO = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_TWO',
      'navigation.hotkeys.accept_call_from_call_list_two',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit2]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 2);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_THREE = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_THREE',
      'navigation.hotkeys.accept_call_from_call_list_three',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit3]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 3);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_FOUR = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_FOUR',
      'navigation.hotkeys.accept_call_from_call_list_four',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit4]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 4);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_FIVE = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_FIVE',
      'navigation.hotkeys.accept_call_from_call_list_five',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit5]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 5);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_SIX = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_SIX',
      'navigation.hotkeys.accept_call_from_call_list_six',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit6]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 6);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_SEVEN = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_SEVEN',
      'navigation.hotkeys.accept_call_from_call_list_seven',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit7]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 7);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_EIGHT = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_EIGHT',
      'navigation.hotkeys.accept_call_from_call_list_eight',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit8]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 8);
         return true;
      }
   );

   static readonly ACCEPT_CALL_FROM_CALL_LIST_NINE = new ShortcutTypeGlobal(
      'ACCEPT_CALL_FROM_CALL_LIST_NINE',
      'navigation.hotkeys.accept_call_from_call_list_nine',
      shortcutStringBuilder(...[Key.Ctrl, Key.Digit9]),
      (store) => {
         ShortcutTypeGlobal.AcceptCallIfPossible(store, 9);
         return true;
      }
   );

   static readonly NEW_EMAIL = new ShortcutTypeGlobal('NEW_EMAIL', 'navigation.hotkeys.new_email', shortcutStringBuilder(...[Key.Alt, Key.KeyE]), (store) => {
      const isFeatureEnabled = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA));
      if (isFeatureEnabled) {
         store.dispatch(new StartEmailProcess(EmailPurpose.COMPOSE_EMAIL));
      }
      return true;
   });

   // STATE
   static readonly CHANGE_STATE_DND = new ShortcutTypeGlobal(
      'CHANGE_STATE_DND',
      'navigation.hotkeys.change_state_dnd',
      shortcutStringBuilder(...[Key.Alt, Key.KeyP]),
      (store) => {
         const allowedToChangeStatus = store.selectSnapshot(SamwinUserStatusSelectors.getAllowedFollowUpState)(UserStatusEnum.DND.value);
         if (allowedToChangeStatus) {
            store.dispatch(new RequestUserStatusChange(UserStatusEnum.DND.value, Originator.User));
         }
         return true;
      }
   );

   static readonly CHANGE_STATE_ACW = new ShortcutTypeGlobal(
      'CHANGE_STATE_ACW',
      'navigation.hotkeys.change_state_acw',
      shortcutStringBuilder(...[Key.Alt, Key.KeyW]),
      (store) => {
         const allowedToChangeStatus = store.selectSnapshot(SamwinUserStatusSelectors.getAllowedFollowUpState)(UserStatusEnum.ACW.value);
         if (allowedToChangeStatus) {
            store.dispatch(new RequestUserStatusChange(UserStatusEnum.ACW.value, Originator.User));
         }
         return true;
      }
   );

   static readonly CHANGE_STATE_LOGGED_OFF = new ShortcutTypeGlobal(
      'CHANGE_STATE_LOGGED_OFF',
      'navigation.hotkeys.change_state_logged_off',
      shortcutStringBuilder(...[Key.Alt, Key.KeyN]),
      (store) => {
         store.dispatch(new RequestUserStatusChange(UserStatusEnum.LOGGED_OFF.value, Originator.User));
         return true;
      }
   );

   static readonly CHANGE_STATE_AVAILABLE = new ShortcutTypeGlobal(
      'CHANGE_STATE_AVAILABLE',
      'navigation.hotkeys.change_state_available',
      shortcutStringBuilder(...[Key.Alt, Key.KeyA]),
      (store) => {
         store.dispatch(new RequestUserStatusChange(UserStatusEnum.AVAILABLE.value, Originator.User));
         return true;
      }
   );

   static AcceptCallIfPossible(store: Store, position: number) {
      const callInfo = store.selectSnapshot(SamwinCallListState.getCallByPositionFn)(position);
      if (!_.isEmpty(callInfo)) {
         store.dispatch(new AcceptCallFromCallList(callInfo, position));
      }
   }

   public executeShortcut(store: Store): boolean {
      console.debug('[Hotkey] execute', this.enumName);
      return this.hotkeyAction(store);
   }
}
